import { useEffect } from "react";

import { useCanvasRuntime } from "@moment/state";

import { useComputeCells } from "../../hooks/useComputeCells";
import type { CanvasRuntimeManager } from "./CanvasRuntimeManager";

export type UseCreateRuntimeOptions = {
	documentId: string;
	pageId: string;
	documentInteractionMode: "static" | "interactive";
};

/**
 * Creates and manages a CanvasRuntimeManager for compute cells.
 *
 * The runtime lifecycle is managed by Jotai's atomFamily, which:
 * - Creates a new runtime when first accessed for a document/page
 * - Caches the runtime by documentId:pageId to persist across re-renders
 * - Automatically disposes the runtime when no components are using it
 *
 * This solves the memory leak issue where runtimes were stored in a module-level
 * Map that was never cleaned up.
 */
export const useCreateRuntime = ({
	documentId,
	pageId,
	documentInteractionMode,
}: UseCreateRuntimeOptions): CanvasRuntimeManager | null => {
	const { compiledCells: cells } = useComputeCells(documentId, pageId);

	// Get or create the runtime via the Jotai atom
	const runtime = useCanvasRuntime<CanvasRuntimeManager>({
		documentId,
		pageId,
		documentInteractionMode,
	});

	// When cells change, sync them to the runtime
	useEffect(() => {
		runtime?.ensureCells(cells);
	}, [runtime, cells]);

	return runtime;
};
